import { useState } from "react";
import { Search, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * DIAGNÓSTICO DE ACESSO.
 *
 * Responde "por que este usuário pode (ou não) executar esta operação?" usando
 * o MESMO mecanismo do runtime: GET /access-center/users/{id}/effective, que
 * devolve o resultado real do Authorization Engine (módulos, ações, escopo e a
 * ORIGEM de cada autorização) — nunca um simulador com regras próprias.
 */
export default function AccessDiagnostic() {
  const [term, setTerm] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payload, setPayload] = useState(null);
  const [moduleKey, setModuleKey] = useState("");
  const [actionKey, setActionKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    const q = term.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setPayload(null);
    setSelected(null);
    try {
      // GET /users respeita o escopo do solicitante; o termo é filtrado
      // localmente (mesmo contrato da Gestão de Usuários).
      const { data } = await api.get("/users");
      const list = Array.isArray(data) ? data : data?.items || [];
      const needle = q.toLowerCase();
      setCandidates(
        list
          .filter(
            (u) =>
              (u.name || "").toLowerCase().includes(needle) ||
              (u.email || "").toLowerCase().includes(needle)
          )
          .slice(0, 10)
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível pesquisar usuários.");
    } finally {
      setLoading(false);
    }
  };

  const inspect = async (user) => {
    setLoading(true);
    setError("");
    setSelected(user);
    try {
      const { data } = await api.get(`/access-center/users/${user.id}/effective`);
      setPayload(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível carregar o acesso efetivo.");
    } finally {
      setLoading(false);
    }
  };

  const state = payload?.state || {};
  const effective = state.effective || payload?.effective || {};
  const modules = effective.modules || {};
  const permissions = effective.permissions || {};
  const scope = effective.scope || {};
  const resolution = effective.module_resolution || {};

  const decision = (() => {
    if (!moduleKey) return null;
    const moduleAllowed = modules[moduleKey] === true;
    if (!actionKey) {
      return {
        allowed: moduleAllowed,
        reason: moduleAllowed
          ? `Módulo concedido (origem: ${resolution[moduleKey] || "política efetiva"}).`
          : `Módulo não disponível para este usuário (origem: ${resolution[moduleKey] || "sem concessão"}).`,
      };
    }
    const actionAllowed = permissions?.[moduleKey]?.[actionKey] === true;
    if (!moduleAllowed) {
      return {
        allowed: false,
        reason: "O módulo não está disponível: nenhuma ação é autorizada.",
      };
    }
    return {
      allowed: actionAllowed,
      reason: actionAllowed
        ? "Ação concedida ao usuário (override ou política efetiva)."
        : "Ação não concedida: depende de override do usuário ou da política do perfil.",
    };
  })();

  return (
    <section className="space-y-4" data-testid="access-diagnostic">
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Stethoscope size={15} className="text-primary" aria-hidden="true" />
          Diagnóstico de acesso
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Resultado real do motor de autorização — o mesmo aplicado pelas APIs.
        </p>
        <div className="mt-2 flex gap-2">
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
            placeholder="Nome ou e-mail do usuário"
            className="h-9"
            data-testid="diagnostic-search-input"
          />
          <Button onClick={search} disabled={loading} className="h-9" data-testid="diagnostic-search-button">
            <Search size={15} className="mr-1.5" aria-hidden="true" /> Buscar
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        {candidates.length > 0 && (
          <ul className="mt-3 divide-y divide-border">
            {candidates.map((user) => (
              <li key={user.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{user.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => inspect(user)} data-testid={`diagnostic-user-${user.id}`}>
                  Diagnosticar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && payload && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">{selected.name}</h3>
            <p className="text-xs text-muted-foreground">{selected.email}</p>
            <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Conta ativa</dt>
                <dd className="font-medium">{String(state.user?.active ?? selected.active ?? true)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Revisão de autorização</dt>
                <dd className="font-medium">{state.authz_revision ?? "-"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Clientes autorizados</dt>
                <dd className="font-medium">{(scope.client_ids || []).length}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Unidades autorizadas</dt>
                <dd className="font-medium">{(scope.unit_ids || []).length}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Módulos disponíveis</dt>
                <dd className="font-medium">
                  {Object.values(modules).filter(Boolean).length} de {Object.keys(modules).length}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">Consultar operação</h3>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Input
                value={moduleKey}
                onChange={(e) => setModuleKey(e.target.value)}
                placeholder="Módulo (ex.: ocorrencias)"
                className="h-9"
                data-testid="diagnostic-module-input"
              />
              <Input
                value={actionKey}
                onChange={(e) => setActionKey(e.target.value)}
                placeholder="Ação (ex.: edit) — opcional"
                className="h-9"
                data-testid="diagnostic-action-input"
              />
            </div>
            {decision && (
              <p
                className={`mt-3 rounded-lg border p-3 text-sm ${
                  decision.allowed
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                    : "border-red-500/40 bg-red-500/10 text-red-700"
                }`}
                data-testid="diagnostic-decision"
              >
                <strong>{decision.allowed ? "Permitido" : "Negado"}</strong> — {decision.reason}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">Módulos e origem da autorização</h3>
            <ul className="mt-2 divide-y divide-border text-xs">
              {Object.entries(modules).map(([key, allowed]) => (
                <li key={key} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="font-medium">{key}</span>
                  <span className="text-muted-foreground">
                    {resolution[key] ? `${resolution[key]} · ` : ""}
                    {allowed ? "concedido" : "negado"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
